import React from "react";
import { Link } from "polyrouter";
import {
  ExpandableCard,
  Field,
  makeColumnContentForCardBase10
} from "collections/Cards";
import { DevicesCardPropsFragment } from "generated/graphql";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { ProductImageInContainer } from "elements";
import { isPolyOS } from "utilities/modelSelectors";

export type DeviceCardProps = DevicesCardPropsFragment;

export const getSoftwareVersion = (
  softwareVersion: string | null,
  softwareBuild: string | null,
  model: string | null
) =>
  isPolyOS(model ?? "") && softwareVersion && softwareBuild
    ? softwareVersion + "-" + softwareBuild
    : softwareVersion;

export const DeviceCard: React.FC<DeviceCardProps & {
  accountProvisioningDisabled?: boolean;
}> = item => {
  const { t } = useTranslation();

  let fields: Array<Field> = [
    {
      key: "device-name",
      label: t("DEVICE_NAME"),
      content: <p>{item.name ?? "-"}</p>
    },
    {
      key: "mac-address",
      label: t("MAC_ADDRESS"),
      content: <p>{handleNullFields(item.macAddress)}</p>
    },
    {
      key: "room-name",
      label: t("ROOM"),
      content: <p>{handleNullFields(item.room?.name ?? "")}</p>
    },
    {
      key: "model",
      label: t("DEVICE_MODEL"),
      content: <p>{handleNullFields(item.hardwareModel)}</p>
    },
    {
      key: "image",
      label: "",
      content: (
        <ProductImageInContainer
          className="mt-0"
          hardwareModel={item.hardwareModel}
          maxHeight={120}
        />
      )
    },
    {
      key: "site",
      label: t("SITE"),
      content: <p>{handleNullFields(item.site?.name)}</p>
    },
    {
      key: "serial-number",
      label: t("SERIAL_NUMBER"),
      content: <p>{handleNullFields(item.serialNumber)}</p>
    },
    {
      key: "internal-ip",
      label: t("IPV4"),
      content: <p>{handleNullFields(item.internalIp)}</p>
    },
    {
      key: "software-version",
      label: t("SOFTWARE_VERSION"),
      content: (
        <p>
          {handleNullFields(
            getSoftwareVersion(
              item.softwareVersion,
              item.softwareBuild,
              item.hardwareModel
            )
          )}
        </p>
      )
    }
  ];

  if (isPolyOS(item.hardwareModel || "")) {
    fields.push({
      key: "provider",
      label: t("PROVIDER"),
      content: <p>{item.activeApplicationName ?? "-"}</p>
    });
  }
  const collapsed = fields.slice(0, 6);
  const rest = fields.slice(6);

  const detailsUrl = `/manage/inventory/details/${item.id}`;
  let collapsedContent = makeColumnContentForCardBase10(
    collapsed,
    5,
    "grid-10",
    true
  );
  collapsedContent = (
    <Link persist={["DeviceFilterState"]} to={detailsUrl}>
      {collapsedContent}
    </Link>
  );
  let expandedContent = makeColumnContentForCardBase10(
    rest,
    5,
    "grid-10 inline"
  );
  expandedContent = (
    <Link persist={["DeviceFilterState"]} to={detailsUrl}>
      {expandedContent}
    </Link>
  );

  return (
    <ExpandableCard collapsedContent={collapsedContent}>
      {expandedContent}
    </ExpandableCard>
  );
};
